import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { MedicinesSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  GhostButton,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { useMedicinesList } from '../../hooks/useMedicines'
import AddMedicineDialog from './components/AddMedicineDialog'
import { parseUtcDate } from '../../utils/dates'

const TodayStrip = styled.div`
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  padding: 16px 20px;
`

const DoseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  & + & {
    border-top: 1px dashed ${(p) => p.theme?.color?.border || '#E6E0D6'};
  }
`

const Check = styled.button`
  appearance: none;
  border: 2px solid ${(p) => p.theme?.color?.borderStrong || '#D5CCBC'};
  background: ${(p) => (p.$on ? p.theme?.color?.accent || '#6B9080' : 'transparent')};
  width: 26px;
  height: 26px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  flex-shrink: 0;
`

const MedCard = styled(Card)`
  cursor: pointer;
  transition: border-color 200ms ease-out;
  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

function timeLabel(iso) {
  if (!iso) return ''
  try {
    return parseUtcDate(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return iso
  }
}

export default function MedicinesPage() {
  const navigate = useNavigate()
  const { list, today, loading, recordIntake, create } = useMedicinesList()
  const [addOpen, setAddOpen] = useState(false)

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care">Back to Care</BackLink>
        <Row $between $wrap>
          <div>
            <Pill>Care</Pill>
            <PageHeading style={{ marginTop: 12 }}>Medicines</PageHeading>
            <PageSubheading>Today&apos;s doses, schedules, intake history.</PageSubheading>
          </div>
          <GhostButton onClick={() => setAddOpen(true)}>+ Add medicine</GhostButton>
        </Row>

        {loading && !list ? (
          <MedicinesSkeleton />
        ) : (
          <>
            {today && today.length > 0 && (
              <TodayStrip>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#6B7280',
                    marginBottom: 8,
                  }}
                >
                  Today
                </h3>
                {today.map((d, i) => (
                  <DoseRow key={`${d.medicineScheduleId}-${i}`}>
                    <Check
                      $on={d.status === 'TAKEN'}
                      onClick={() =>
                        d.status !== 'TAKEN' &&
                        recordIntake(d.medicineScheduleId, {
                          status: 'TAKEN',
                          scheduledFor: d.scheduledFor,
                          actualTakenAt: new Date().toISOString(),
                        })
                      }
                      aria-label={d.status === 'TAKEN' ? 'Taken' : `Mark ${d.medicineName} taken`}
                    >
                      {d.status === 'TAKEN' ? '✓' : ''}
                    </Check>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {d.medicineName}{' '}
                        <span style={{ color: '#6B7280', fontWeight: 400 }}>· {d.dosage}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>
                        {timeLabel(d.scheduledFor)}
                      </div>
                    </div>
                  </DoseRow>
                ))}
              </TodayStrip>
            )}

            {!list?.length ? (
              <div
                style={{
                  padding: '48px 24px',
                  textAlign: 'center',
                  border: '1px dashed rgba(31,41,55,0.18)',
                  borderRadius: 20,
                }}
              >
                <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No medicines yet.</p>
                <Muted>
                  Practitioner-prescribed medicines show up automatically. You can also add ones you
                  take on your own.
                </Muted>
              </div>
            ) : (
              <Stack $gap="10px">
                {list.map((m) => (
                  <MedCard
                    key={m.id}
                    onClick={() => navigate(`/patient-home/care/medicines/${m.id}`)}
                  >
                    <Row $between $gap="10px">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 16, color: '#1F2937' }}>
                          {m.name}{' '}
                          <span style={{ color: '#6B7280', fontWeight: 400 }}>· {m.dosage}</span>
                        </div>
                        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                          {m.frequencyType === 'DAILY'
                            ? `Daily · ${(m.timesOfDay || []).join(', ')}`
                            : m.frequencyType === 'WEEKLY'
                            ? `Weekly · ${(m.daysOfWeek || []).join(', ')}`
                            : 'As needed'}
                          {m.prescribedBy && m.prescribedBy !== 'self'
                            ? ` · by ${m.prescribedBy}`
                            : ' · self'}
                        </div>
                      </div>
                      <Pill>View</Pill>
                    </Row>
                  </MedCard>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>

      <AddMedicineDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={async (body) => {
          await create(body)
        }}
      />
    </ContentMaxWidth>
  )
}
